import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, CalendarPlus, Check, Loader2, X, MapPinCheck, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { get, post, errMsg, hzFile } from "@/lib/api";
import { APPT_STATUS, WEEKDAYS_SHORT, longDate, googleCalUrl, todayIso } from "@/lib/hz";

export const useApptRefresh = () => {
  const qc = useQueryClient();
  return () => ["appointment", "agenda", "slots", "jobs"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
};

export const ApptBadge = ({ status }) => {
  const s = APPT_STATUS[status] ?? APPT_STATUS.pendiente;
  return <span data-testid="appointment-status" className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
};

export const CalendarExport = ({ a, other }) =>
  a.status !== "confirmada" ? null : (
    <div className="mt-3 flex flex-wrap gap-2" data-testid={`calendar-export-${a.id}`}>
      <Button asChild variant="outline" size="sm" className="flex-1 rounded-lg"><a href={googleCalUrl(a, other)} target="_blank" rel="noreferrer" data-testid={`calendar-google-${a.id}`}><CalendarPlus className="text-sky-600" />Google Calendar</a></Button>
      <Button asChild variant="outline" size="sm" className="flex-1 rounded-lg"><a href={hzFile(`/appointments/${a.id}/ics`)} download data-testid={`calendar-ics-${a.id}`}><Download className="text-orange-600" />Calendario (.ics)</a></Button>
    </div>
  );

export const CheckIn = ({ a }) => {
  const refresh = useApptRefresh();
  const [busy, setBusy] = useState(false);
  if (a.arrived_at) return <div className={`mt-2 text-sm font-semibold ${a.punctual ? "text-emerald-700" : "text-amber-700"}`}>Llegó a las {a.arrived_at.slice(11, 16)} · {a.punctual ? "puntual" : `${a.delay_min} min de retraso`}</div>;
  if (a.status !== "confirmada" || a.date !== todayIso()) return null;
  const go = async () => {
    setBusy(true);
    try { const r = await post(`/appointments/${a.id}/check-in`); refresh(); toast.success(r.punctual ? "Llegada registrada. ¡Puntual! Hemos avisado al cliente." : `Llegada registrada con ${r.delay_min} min de retraso. Hemos avisado al cliente.`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button className="mt-3 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={go} data-testid={`check-in-btn-${a.id}`}>{busy ? <Loader2 className="animate-spin" /> : <MapPinCheck />}He llegado</Button>;
};

const SlotPicker = ({ jobId, cta, onPick, pending }) => {
  const q = useQuery({ queryKey: ["slots", jobId], queryFn: () => get(`/jobs/${jobId}/slots?days=14`) });
  const [day, setDay] = useState(null);
  const [start, setStart] = useState(null);
  if (q.isLoading) return <div className="h-28 animate-pulse rounded-2xl bg-slate-100" />;
  if (q.isError) return <p className="text-sm text-rose-600">{errMsg(q.error, "No se pudo cargar la disponibilidad")}</p>;
  const days = q.data ?? [];
  const first = days.find((d) => d.slots.length)?.date;
  if (!first) return <p className="text-sm text-slate-500" data-testid="no-slots">El profesional no tiene huecos libres en las próximas 2 semanas. Escríbele por el chat.</p>;
  const sel = day ?? first;
  const cur = days.find((d) => d.date === sel);
  return (
    <div className="space-y-3" data-testid="slot-picker">
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {days.map((d) => {
          const has = d.slots.length > 0;
          return (
            <button key={d.date} type="button" disabled={!has} onClick={() => { setDay(d.date); setStart(null); }} data-testid={`slot-day-${d.date}`}
              className={`flex w-12 shrink-0 flex-col items-center rounded-xl border-2 py-1.5 text-xs transition-colors ${sel === d.date ? "border-orange-500 bg-orange-50 text-orange-800" : has ? "border-slate-200 hover:border-slate-300" : "border-transparent text-slate-300"}`}>
              <span className="font-semibold">{WEEKDAYS_SHORT[d.weekday]}</span><span className="font-heading text-base font-bold">{Number(d.date.split("-")[2])}</span>
            </button>
          );
        })}
      </div>
      {cur && (
        <>
          <div className="text-sm font-semibold text-slate-700 first-letter:uppercase">{longDate(cur.date)}</div>
          <div className="grid grid-cols-3 gap-1.5">
            {cur.slots.map((s) => (
              <button key={s.start} type="button" onClick={() => setStart(s.start)} data-testid={`slot-${cur.date}-${s.start}`}
                className={`rounded-lg border py-1.5 text-sm font-semibold transition-colors ${start === s.start ? "border-orange-600 bg-orange-600 text-white" : "border-slate-200 hover:border-orange-300"}`}>{s.start}</button>
            ))}
          </div>
        </>
      )}
      <Button className="w-full rounded-xl" disabled={!start || pending} onClick={() => onPick({ date: sel, start })} data-testid="slot-submit-btn">{pending ? <Loader2 className="animate-spin" /> : <CalendarDays />}{cta}</Button>
    </div>
  );
};

export const AppointmentPanel = ({ jobId, isPro, canBook }) => {
  const q = useQuery({ queryKey: ["appointment", jobId], queryFn: () => get(`/jobs/${jobId}/appointment`), retry: false });
  const refresh = useApptRefresh();
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(null);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); refresh(); setChanging(false); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); refresh(); } finally { setBusy(null); }
  };
  if (q.isLoading) return <div className="h-24 animate-pulse rounded-3xl bg-slate-100" />;
  const raw = q.isError ? null : q.data ?? null;
  const a = raw && raw.status !== "cancelada" ? raw : null;
  if (!a && (isPro || !canBook)) {
    return isPro && canBook ? (
      <div className="rounded-3xl border border-orange-200 bg-white p-6" data-testid="appointment-panel"><div className="flex items-center gap-2 font-heading text-lg font-bold"><CalendarDays className="size-5 text-orange-600" />Cita</div><p className="mt-2 text-sm text-slate-600" data-testid="appointment-none">El cliente aún no ha elegido día y hora según tu disponibilidad.</p></div>
    ) : null;
  }
  const canConfirm = a && ((a.status === "pendiente" && isPro) || (a.status === "propuesta" && !isPro));
  const pick = (body) => isPro
    ? run("p", () => post(`/appointments/${a.id}/propose`, body), "Nuevo horario propuesto al cliente")
    : run("b", () => post(`/jobs/${jobId}/appointments`, body), "Cita solicitada. El profesional la confirmará en breve.");
  return (
    <div className="rounded-3xl border border-orange-200 bg-white p-6" data-testid="appointment-panel">
      <div className="flex items-center gap-2 font-heading text-lg font-bold"><CalendarDays className="size-5 text-orange-600" />Cita</div>
      {a ? (
        <div className="mt-3 rounded-2xl bg-orange-50/60 p-4" data-testid="appointment-current">
          <ApptBadge status={a.status} />
          <div className="mt-2 font-heading text-lg font-bold first-letter:uppercase" data-testid="appointment-date">{longDate(a.date)}</div>
          <div className="text-sm font-semibold text-slate-700" data-testid="appointment-time">{a.start} – {a.end}</div>
          {a.status === "pendiente" && !isPro && <p className="mt-1 text-xs text-slate-500">Esperando confirmación de {a.pro_name}.</p>}
          {a.status === "propuesta" && isPro && <p className="mt-1 text-xs text-slate-500">Esperando que {a.client_name} acepte el nuevo horario.</p>}
          {a.status === "confirmada" && <p className="mt-1 text-xs text-emerald-700">Os enviaremos un recordatorio por correo el día anterior.</p>}
          <CalendarExport a={a} other={isPro ? a.client_name : a.pro_name} />
          {isPro && <CheckIn a={a} />}
        </div>
      ) : <p className="mt-2 text-sm text-slate-600">Elige un hueco libre en la agenda de tu profesional.</p>}
      {canConfirm && !changing && (
        <Button className="mt-3 w-full rounded-xl" disabled={!!busy} onClick={() => run("c", () => post(`/appointments/${a.id}/confirm`), "Cita confirmada")} data-testid="appointment-confirm-btn">
          {busy === "c" ? <Loader2 className="animate-spin" /> : <Check />}{isPro ? "Confirmar cita" : "Aceptar nuevo horario"}
        </Button>
      )}
      {changing || (!a && canBook) ? (
        <div className="mt-4">
          <SlotPicker jobId={jobId} pending={busy === "p" || busy === "b"} cta={isPro ? "Proponer este horario" : a ? "Solicitar este horario" : "Solicitar cita"} onPick={pick} />
          {changing && <Button variant="ghost" className="mt-2 w-full" onClick={() => setChanging(false)} data-testid="slot-cancel-btn">Volver</Button>}
        </div>
      ) : a && canBook && a.status !== "cancelada" && (
        <div className="mt-3 flex gap-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setChanging(true)} data-testid="appointment-change-btn">{isPro ? "Proponer otro horario" : "Cambiar horario"}</Button>
          <Button variant="ghost" size="icon" className="text-rose-600" disabled={!!busy} aria-label="Cancelar cita" data-testid="appointment-cancel-btn"
            onClick={() => run("x", () => post(`/appointments/${a.id}/cancel`), "Cita cancelada")}><X /></Button>
        </div>
      )}
    </div>
  );
};
